import React, { useEffect, useMemo, useRef, useState } from 'react';
import stringify from 'utils/stringify';
import uuidFromString from 'utils/uuidFromString';
import httpRequest from 'utils/httpRequest';
import store from 'utils/store';
import produce from 'utils/produce';

import { Spinner } from 'components/base';

import { formatData } from 'editor-utils/dataParser';
import pick from 'utils/pick';
import SETTINGS from '../../settings';

const CACHE_VERSION = 2;
const CACHE_KEY = `editor_pdfer_preview_urls:${CACHE_VERSION}`;

const getKey = data => {
  return uuidFromString(stringify(data));
};

const getCache = () => store.session.get(CACHE_KEY, {});

const getPreviewUrl = async (data, options = {}) => {
  const key = getKey(data);

  const cache = getCache();
  if (cache[key]) {
    if (cache[key] === 'loading') {
      const previewUrl = await new Promise(resolve => {
        let iteration = 0;
        const intervalId = setInterval(() => {
          const latestCache = getCache();
          if (iteration < 100 && latestCache[key]) {
            iteration += 1;
            if (latestCache[key] !== 'loading') {
              clearInterval(intervalId);
              resolve(latestCache[key]);
            }
          } else {
            clearInterval(intervalId);
            resolve(null);
          }
        }, 100);
      });
      if (previewUrl) {
        return previewUrl;
      }
    } else {
      return cache[key];
    }
  }

  store.session.set(
    CACHE_KEY,
    produce(getCache(), draft => {
      draft[key] = 'loading';
    }),
    true,
  );

  const getImagePreviewUrl = async () => {
    const getOptions = sync => ({
      method: 'POST',
      body: {
        data: {
          ...data,
          sync,
          format: 'jpg'
        },
      },
      ...options,
    });

    let response;

    try {
      response = await httpRequest(SETTINGS.POLOTNO_REMOTE_PDFER_URL, getOptions(true));
    } catch (e) {
      if (e.code === 413) {
        // do this repeat only in case of large payload
        response = await httpRequest(SETTINGS.POLOTNO_REMOTE_PDFER_URL, getOptions(false));
      } else {
        throw e;
      }
    }

    const json = await response.json();
    return json.preview_url;
  };

  try {
    const previewUrl = await getImagePreviewUrl();

    store.session.set(
      CACHE_KEY,
      produce(getCache(), draft => {
        draft[key] = previewUrl;
      }),
      true,
    );

    return previewUrl;
  } catch (e) {
    store.session.set(
      CACHE_KEY,
      produce(getCache(), draft => {
        delete draft[key];
      }),
      true,
    );
    throw e;
  }
};

const PageServerSidePreview = props => {
  const { visible } = props;

  const stringifiedData = useMemo(() => {
    // use only visible params
    let page = pick(props.page, 'id', 'placeholders', 'override', 'filter', 'spine');

    // when we want only small thumb – it's not needed to update due to changing page-num
    if (props.dpi < 36) {
      page = produce(page, draft => {
        draft.placeholders = page.placeholders.filter(p => p.id !== 'page-num');
      });
    }

    const data = formatData(
      props.template,
      {
        pages: props.page.type === 'pages' && props.template.cover ? [null, page] : [page],
        photos: props.photos,
      },
      {
        withPageParams: ['spine'],
      },
    );

    // we don't need to send all data to server
    return stringify(pick(data, 'cover', 'pages'));
  }, [props.template, props.page, props.photos, props.dpi]);

  const [state, setState] = useState('loading');
  const [imageUrl, setImageUrl] = useState();

  const abortControllerHeadRef = useRef();

  useEffect(() => {
    if (state === 'error') {
      try {
        gtag('event', 'page_preview_error', {
          event_category: 'editor',
        });
      } catch (e) { }
    }
  }, [state]);

  useEffect(() => {
    setImageUrl(null);
  }, [stringifiedData, props.template.id, props.template.version, props.dpi]);

  useEffect(() => {
    if (imageUrl || state === 'error' || !visible) {
      return () => { };
    }

    setState('loading');

    const abortController = new AbortController();
    (async () => {
      try {
        const previewUrl = await getPreviewUrl(
          {
            product_id: props.template.id,
            product_version: props.template.version,
            product_data: JSON.parse(stringifiedData),
            dpi: props.dpi,
          },
          { signal: abortController.signal },
        );
        setImageUrl(previewUrl);
      } catch (e) {
        if (e.message === 'AbortError' || e.name === 'AbortError') {
          return;
        }
        setState('error');
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [imageUrl, state, visible]);

  useEffect(() => {
    if (!imageUrl || !visible) {
      return () => { };
    }

    // Abort any previous HEAD request
    if (abortControllerHeadRef.current) {
      abortControllerHeadRef.current.abort();
    }

    abortControllerHeadRef.current = new AbortController();
    const currentImageUrl = imageUrl; // Capture current URL to prevent race conditions

    (async () => {
      try {
        const response = await httpRequest(currentImageUrl, {
          mode: 'cors',
          method: 'HEAD',
          maximumAttempts: 100,
          retryInAnyCase: true,
          useLinearBackoff: true,
          signal: abortControllerHeadRef.current.signal,
          logError: false,
        });

        // Only update state if this is still the current image URL
        if (currentImageUrl === imageUrl && response.status === 200) {
          setState('done');
          return;
        }
      } catch (e) {
        if (e.message === 'AbortError' || e.name === 'AbortError') {
          return;
        }

        setState('error');
        setImageUrl(null);
      }
    })();

    return () => {
      if (abortControllerHeadRef.current) {
        abortControllerHeadRef.current.abort();
      }
    };
  }, [imageUrl, visible]);

  return (
    <>
      {(state === 'done' || !visible) && imageUrl && (
        <img
          src={imageUrl}
          className="page-preview-full"
          alt=""
          title=""
          onLoad={e => {
            setState('done');
            props.onLoad({ width: e.target.naturalWidth, height: e.target.naturalHeight });
            if (abortControllerHeadRef.current) {
              abortControllerHeadRef.current.abort();
            }
          }}
        />
      )}

      {state === 'loading' && (
        <>
          <div
            className="position-absolute w-100 h-100"
            style={{ left: 0, top: 0, background: '#FFFFFF66' }}
          />
          <div className="position-absolute d-flex align-items-center justify-content-center w-100 h-100">
            <Spinner appearance="grow" />
          </div>
        </>
      )}

      {state === 'error' && (
        <>
          <div
            className="position-absolute w-100 h-100"
            style={{ left: 0, top: 0, background: '#FFFFFF66' }}
          />
          <div className="position-absolute text-center w-100" style={{ top: '40%' }}>
            <h1 className="text-danger">
              Oops, something went wrong.
            </h1>
            <button
              className="btn btn-primary mt-3"
              onClick={(e) => {
                e.stopPropagation();
                // Reset state to trigger a reload
                setState('loading');
                setImageUrl(null);
              }}
            >
              Try Again
            </button>
          </div>
        </>
      )}
    </>
  );
};

export default PageServerSidePreview;
